import React, { useState } from 'react';
import { EditorView } from './ai-scan/EditorView';
import { ProductData } from '../../types';
import { ChevronLeft, Edit3, Layers, Package, CheckCircle2, Trash2, Plus, Camera } from 'lucide-react';

interface ScannedProduct {
  id: string;
  images: string[];
  productData: ProductData;
}

interface BulkReviewProps {
  products: ScannedProduct[];
  onUpdateProduct: (id: string, data: ProductData) => void;
  onRemoveProduct?: (id: string) => void;
  onAddMore?: () => void;
  onPublishAll: () => void;
  onBack: () => void;
  isPublishing: boolean;
}

export const BulkReview: React.FC<BulkReviewProps> = ({
  products,
  onUpdateProduct,
  onRemoveProduct,
  onAddMore,
  onPublishAll,
  onBack,
  isPublishing,
}) => {
  const [editingId, setEditingId] = useState<string | null>(null);

  if (editingId) {
    const product = products.find(p => p.id === editingId);
    if (!product) {
      setEditingId(null);
      return null;
    }
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => setEditingId(null)}
            className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            <ChevronLeft className="w-4 h-4" /> Back to Review
          </button>
          <span className="text-sm font-medium text-gray-500">Editing Product</span>
        </div>
        <div className="flex-1 overflow-y-auto">
          <EditorView
            initialData={product.productData}
            onConfirm={(updatedData) => {
              onUpdateProduct(editingId, updatedData);
              setEditingId(null);
            }}
          />
        </div>
      </div>
    );
  }

  const totalImages = products.reduce((sum, p) => sum + p.images.length, 0);

  return (
    <div className="flex flex-col h-full gap-4 animate-in fade-in duration-300">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">
          <ChevronLeft className="w-4 h-4" /> Back
        </button>
        <div className="flex items-center gap-3 text-sm text-gray-500">
          <span className="flex items-center gap-1"><Package className="w-4 h-4" /> {products.length} products</span>
          <span className="flex items-center gap-1"><Layers className="w-4 h-4" /> {totalImages} images</span>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto space-y-3">
        {products.map((product, index) => (
          <div
            key={product.id}
            onClick={() => setEditingId(product.id)}
            className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-3 sm:p-4 cursor-pointer hover:border-blue-300 dark:hover:border-blue-700 hover:shadow-sm transition-all"
          >
            <div className="flex gap-3 sm:gap-4">
              {/* Thumbnail */}
              <div className="relative shrink-0">
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700">
                  <img
                    src={product.images[0]}
                    alt={product.productData.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                {product.images.length > 1 && (
                  <span className="absolute -top-1.5 -right-1.5 bg-blue-600 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
                    {product.images.length}
                  </span>
                )}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-1 sm:gap-2">
                  <div className="min-w-0 flex-1">
                    <h4 className="font-semibold text-gray-900 dark:text-white text-sm truncate">
                      {product.productData.title || `Product ${index + 1}`}
                    </h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                      ₦{product.productData.price?.toLocaleString()} · {product.productData.category}
                    </p>
                  </div>
                  <div className="flex items-center gap-0.5 sm:gap-1 shrink-0">
                    {onRemoveProduct && (
                      <button
                        onClick={(e) => { e.stopPropagation(); onRemoveProduct(product.id); }}
                        className="p-1.5 sm:p-2 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                        title="Remove product"
                      >
                        <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-red-400 hover:text-red-600" />
                      </button>
                    )}
                    <button
                      onClick={(e) => { e.stopPropagation(); setEditingId(product.id); }}
                      className="p-1.5 sm:p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                      <Edit3 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400" />
                    </button>
                  </div>
                </div>
                {/* Image strip */}
                {product.images.length > 1 && (
                  <div className="flex gap-1 mt-2 overflow-x-auto">
                    {product.images.slice(0, 5).map((img, i) => (
                      <div key={i} className="w-6 h-6 sm:w-7 sm:h-7 rounded border border-gray-200 dark:border-gray-700 overflow-hidden shrink-0">
                        <img src={img} alt="" className="w-full h-full object-cover" />
                      </div>
                    ))}
                    {product.images.length > 5 && (
                      <span className="w-6 h-6 sm:w-7 sm:h-7 rounded bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-[10px] font-bold text-gray-500 shrink-0">
                        +{product.images.length - 5}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Publish bar */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 sticky bottom-0 space-y-3">
        {onAddMore && products.length < 10 && (
          <button
            onClick={onAddMore}
            disabled={isPublishing}
            className="w-full py-2.5 border-2 border-dashed border-gray-300 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-500 text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 font-medium rounded-xl transition-colors text-sm flex items-center justify-center gap-2"
          >
            <Camera className="w-4 h-4" /> Add More Products ({10 - products.length} slots left)
          </button>
        )}
        <button
          onClick={onPublishAll}
          disabled={isPublishing}
          className="w-full py-3.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-bold rounded-xl transition-colors text-base flex items-center justify-center gap-2"
        >
          {isPublishing ? (
            <><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> Publishing...</>
          ) : (
            <><CheckCircle2 className="w-5 h-5" /> Publish All ({products.length} products)</>
          )}
        </button>
      </div>
    </div>
  );
};
